import {
  Action,
  combineReducers,
  configureStore,
  ThunkAction,
} from '@reduxjs/toolkit';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector,
  useStore,
} from 'react-redux';
import createSagaMiddleware from 'redux-saga';
import responsiveLayoutReducer from './slices/responsiveLayoutSlice';
import drawerContainerReducer from './slices/drawerContainerSlice';
import fileUploadReducer from './slices/fileUploadSlice';
import midiBlockReducer from './slices/midiBlockSlice';
import midiListenerReducer from './slices/midiListenerSlice';
import modalContainerReducer from '../components/utilComponents/modalContainer/modalContainerSlice';
import notificationReducer from './slices/notificationSlice';
import globalSettingsReducer from './slices/globalSettingsSlice';
import joyrideTourReducer from './slices/joyrideTourSlice';
import userActivityReducer from './slices/userActivitySlice';
import osmdStateReducer from './slices/osmdStateSlice';
import customThemeReducer from './slices/customThemeSlice';
import { persistStore, persistReducer } from 'redux-persist';
import rootSaga from './sagas';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web

const rootReducer = combineReducers({
  midiBlock: midiBlockReducer,
  responsiveLayout: responsiveLayoutReducer,
  midiListener: midiListenerReducer,
  modalContainer: modalContainerReducer,
  drawerContainer: drawerContainerReducer,
  globalSettings: globalSettingsReducer,
  joyrideTour: joyrideTourReducer,
  userActivity: userActivityReducer,
  fileUpload: fileUploadReducer,
  notification: notificationReducer,
  osmdState: osmdStateReducer,
  customTheme: customThemeReducer,
});

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['userActivity'], // only these slices will be persisted
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const sagaMiddleware = createSagaMiddleware();

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST'],
      },
    }).concat(sagaMiddleware as any),
});
let persistor = persistStore(store);

sagaMiddleware.run(rootSaga);

export { store, persistor };

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppStore = typeof store;

// Modern React-Redux 9.1+ typed hooks using .withTypes()
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
export const useAppStore = useStore.withTypes<AppStore>();

// Legacy hook for backward compatibility (can be removed later)
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

// ThunkAction type is still valid but consider migrating to createAsyncThunk
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
